/**
 * Service centralisé pour la gestion des tenants (organisations)
 * Ce service facilite la création, mise à jour et gestion des tenants
 * tout en assurant la cohérence entre les différentes méthodes d'accès.
 */

import { Tenant, TenantPlan, TenantFeatures } from './types';
// Define TenantStatus as an enum with values
export enum TenantStatus {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
  PENDING = 'pending',
  SUSPENDED = 'suspended'
}
import tenantAPI from './tenant-api';
import tenantManager from './tenant-manager';
import UserDataManager from '../auth/user-data-manager';
import { toast } from 'react-hot-toast';
import { AxiosError } from 'axios';

/**
 * Helper pour charger TokenManager dynamiquement
 * Cela évite les problèmes de dépendance circulaire
 */
async function loadTokenManager() {
  try {
    const module = await import('../auth/token-manager');
    return module.TokenManager;
  } catch (error) {
    console.error('Erreur lors du chargement de TokenManager:', error);
    throw error;
  }
}

/**
 * Service de gestion des tenants (organisations)
 */
export class TenantService {
  private static instance: TenantService;
  private tenants: Tenant[] = [];
  // Cast the tenantAPI to ensure TypeScript recognizes all its methods
  private tenantAPI = tenantAPI as unknown as {
    getAllTenants(): Promise<Tenant[]>;
    getUserTenants(): Promise<Tenant[]>;
    getUserTenantData(): Promise<any | null>;
    getTenant(tenantId: string): Promise<Tenant | null>;
    setDefaultTenant(tenantId: string): Promise<{ success: boolean; message: string }>;
    createTenant(tenantData: Partial<Tenant>, userId?: string): Promise<Tenant>;
    inviteUserToTenant(tenantId: string, email: string, role?: string): Promise<{ success: boolean; message: string }>;
    deleteTenant(tenantId: string): Promise<{ success: boolean; message: string }>;
    userCache: Record<string, any>;
    CACHE_TTL: number;
  };
  
  /**
   * Récupère tous les tenants disponibles
   * @returns Promise<Tenant[]> Liste des tenants
   */
  public async getAllTenants(): Promise<Tenant[]> {
    try {
      console.log('🏘️ TenantService - Récupération de tous les tenants...');
      // Vérifier si nous avons déjà les tenants en cache
      if (this.tenants && this.tenants.length > 0) {
        console.log(`🏘️ TenantService - Utilisation du cache (${this.tenants.length} tenants)`);
        return this.filterTestTenants(this.tenants);
      }
      
      // Sinon, récupérer depuis l'API
      console.log('🏘️ TenantService - Récupération depuis l\'API...');
      const apiTenants = await this.tenantAPI.getUserTenants();
      
      // Mettre à jour le cache
      this.tenants = apiTenants;
      
      console.log(`🏘️ TenantService - Tenants récupérés: ${apiTenants.length}`);
      return this.filterTestTenants(apiTenants);
    } catch (error) {
      console.error('❌ TenantService - Erreur lors de la récupération des tenants:', error);
      
      // En cas d'erreur, retourner les tenants en cache ou un tableau vide
      return this.tenants ? this.filterTestTenants(this.tenants) : [];
    }
  };

  private constructor() {}

  /**
   * Retourne l'instance unique du service (Singleton)
   */
  public static getInstance(): TenantService {
    if (!TenantService.instance) {
      TenantService.instance = new TenantService();
    }
    return TenantService.instance;
  }
  
  /**
   * Génère un slug à partir d'un nom
   * @param name Le nom à partir duquel générer le slug
   * @returns Le slug généré
   */
  private generateSlug(name: string): string {
    return (
      name
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)/g, '') +
      '-' +
      Math.random().toString(36).substring(2, 10)
    );
  }

  /**
   * Vérifie si un tenant est un tenant de test
   * @param tenant Le tenant à vérifier
   * @returns true si c'est un tenant de test
   */
  private isTestTenant(tenant: Tenant): boolean {
    // Vérifier que tenant.id est bien une chaîne de caractères
    const isIdString = typeof tenant.id === 'string';
    const idString = isIdString ? tenant.id : String(tenant.id);
    
    return (
      // Utiliser la version string-safe de l'ID
      (isIdString && tenant.id.includes('test')) ||
      ((tenant.name?.toLowerCase().includes('test') ?? false) || false) ||
      /(001|002|003)$/.test(idString)
    );
  }
  
  /**
   * Vérifie si l'application est actuellement limitée par le rate limiter
   * @returns true si l'application est actuellement limitée
   */
  private checkRateLimit(): boolean {
    try {
      const rateLimitData = localStorage.getItem('sentinel_rate_limit_data');
      
      if (rateLimitData) {
        const { timestamp, retryAfter } = JSON.parse(rateLimitData);
        const now = Date.now();
        
        if (now < timestamp + retryAfter) {
          // Toujours en période de limitation
          const remainingSeconds = Math.ceil((timestamp + retryAfter - now) / 1000);
          console.log(`⚠️ TenantService - Rate limiting actif pour encore ${remainingSeconds} secondes`);
          throw new Error(`Trop de requêtes. Veuillez réessayer dans ${remainingSeconds} secondes.`);
        } else {
          // La période de limitation est terminée
          localStorage.removeItem('sentinel_rate_limit_data');
        }
      }
      
      return false;
    } catch (e: unknown) {
      if (e instanceof Error && e.message.includes('Trop de requêtes')) {
        throw e; // Propager l'erreur de limitation
      }
      
      console.warn('Erreur lors de la vérification du rate limiting:', e);
      return false;
    }
  }
  
  /**
   * Marque l'application comme étant limitée par le rate limiter
   * @param retryAfter Délai en ms avant de pouvoir réessayer
   */
  private markRateLimited(retryAfter: number): void {
    try {
      localStorage.setItem('sentinel_rate_limit_data', JSON.stringify({
        timestamp: Date.now(),
        retryAfter: retryAfter
      }));
    } catch (e: unknown) {
      console.error('Erreur lors du marquage de limitation de taux:', e);
    }
  }
  
  /**
   * Réinitialise les compteurs de tentatives
   */
  private resetRateLimitCounters(): void {
    try {
      localStorage.removeItem('sentinel_tenant_creation_attempts');
    } catch (e: unknown) {
      console.error('Erreur lors de la réinitialisation des compteurs:', e);
    }
  }

  /**
   * Récupère l'ID de l'utilisateur actuellement connecté
   * @returns L'ID de l'utilisateur ou undefined si non connecté
   */
  private async getCurrentUserId(): Promise<string | undefined> {
    try {
      const tokenManager = await loadTokenManager();
      if (!tokenManager.isAuthenticated()) {
        return undefined;
      }
      
      const userData = UserDataManager.getUserData();
      // Ensure id is returned as string
      return userData?.id ? String(userData.id) : undefined;
    } catch (error) {
      console.error('Erreur lors de la récupération de l\'ID utilisateur:', error);
      return undefined;
    }
  }
  
  /**
   * Filtre les tenants de test en production
   * @param tenants Liste des tenants à filtrer
   * @returns Liste des tenants filtrés
   */
  private filterTestTenants(tenants: Tenant[]): Tenant[] {
    // En production, filtrer les tenants de test
    if (process.env.NODE_ENV === 'production') {
      return tenants.filter(tenant => !this.isTestTenant(tenant));
    }
    
    return tenants;
  }

  /**
   * Récupère un tenant spécifique par son ID
   * @param tenantId ID du tenant à récupérer
   * @returns Le tenant ou null si non trouvé
   */
  public async getTenantById(tenantId: string): Promise<Tenant | null> {
    try {
      if (!tenantId) {
        console.warn('⚠️ TenantService: ID de tenant invalide');
        return null;
      }
      
      // Vérifier d'abord si le tenant est déjà en cache local
      const cachedTenants = this.tenants || [];
      const cachedTenant = cachedTenants.find(t => t.id === tenantId);
      
      if (cachedTenant) {
        console.log('🏘️ TenantService: Tenant trouvé dans le cache local');
        return cachedTenant;
      }
      
      // Sinon, essayer de récupérer depuis l'API
      console.log(`🏘️ TenantService: Récupération du tenant ${tenantId} via l'API`);
      const tenant = await this.tenantAPI.getTenant(tenantId);
      
      // Mettre à jour le cache si le tenant est trouvé
      if (tenant) {
        // Ajouter au cache local
        const updatedTenants = [...this.tenants];
        const existingIndex = updatedTenants.findIndex(t => t.id === tenant.id);
        
        if (existingIndex >= 0) {
          updatedTenants[existingIndex] = { ...updatedTenants[existingIndex], ...tenant };
        } else {
          updatedTenants.push(tenant);
        }
        
        this.tenants = updatedTenants;
      }
      
      return tenant;
    } catch (error) {
      console.error('❌ TenantService: Erreur lors de la récupération du tenant par ID:', error);
      return null;
    }
  }

  /**
   * Définit un tenant comme tenant par défaut pour l'utilisateur courant
   * @param tenantId ID du tenant à définir comme défaut
   * @returns true si l'opération a réussi, false sinon
   */
  public async setDefaultTenant(tenantId: string): Promise<boolean> {
    try {
      console.log(`🏘️ TenantService: Définition du tenant ${tenantId} comme tenant par défaut`);
      
      // Vérifier si l'utilisateur est authentifié
      try {
        const tokenManager = await loadTokenManager();
        if (!tokenManager.isAuthenticated()) {
          console.warn('🔴 TenantService: Tentative de vérification de plan sans authentification');
          return false;
        }
      } catch (error) {
        console.error('Erreur lors de la vérification de l\'authentification:', error);
        return false;
      }
      
      // Vérifier si le tenant existe
      const tenant = await this.getTenantById(tenantId);
      if (!tenant) {
        throw new Error(`Le tenant avec l'ID ${tenantId} n'existe pas`);
      }
      
      // Appel API pour définir le tenant par défaut
      const result = await this.tenantAPI.setDefaultTenant(tenantId);
      
      if (result.success) {
        // Mettre à jour le localStorage
        localStorage.setItem('currentTenantId', tenantId);
        localStorage.setItem('sentinel_default_tenant_id', tenantId);
        
        // Déclencher un événement pour informer l'application que le tenant par défaut a changé
        const event = new CustomEvent('tenantDefaultChanged', { detail: { tenantId } });
        window.dispatchEvent(event);
        
        console.log(`✅ TenantService: Tenant ${tenantId} défini comme tenant par défaut avec succès`);
        return true;
      } else {
        throw new Error(result.message || 'Erreur lors de la définition du tenant par défaut');
      }
    } catch (error) {
      console.error('❌ TenantService: Erreur lors de la définition du tenant par défaut:', error);
      
      // En cas d'échec de l'API, tenter de mettre à jour uniquement le localStorage
      try {
        localStorage.setItem('currentTenantId', tenantId);
        localStorage.setItem('sentinel_default_tenant_id', tenantId);
        console.log('⚠️ TenantService: Mise à jour du localStorage malgré l\'échec de l\'API');
      } catch (storageError) {
        console.error('❌ TenantService: Échec également de la mise à jour du localStorage:', storageError);
      }
      
      return false;
    }
  }

  /**
   * Crée un nouveau tenant (organisation) et synchronise les contextes
   * @param tenantData Les données du tenant à créer
   * @param tryAlternativePlans Si true, essaie automatiquement des plans alternatifs en cas d'échec
   * @returns Le tenant créé
   */
  /**
   * Synchronise les tenants entre le localStorage et l'API
   * Cette fonction est appelée au chargement de l'application et après les changements d'authentification
   * @returns Promise<void>
   */
  public async synchronizeTenants(): Promise<void> {
    try {
      console.log('🔄 TenantService - Synchronisation des tenants...');
      
      // Récupérer les tenants depuis l'API
      const apiTenants = await this.tenantAPI.getUserTenants();
      
      // Mettre à jour le cache local
      this.tenants = apiTenants;
      
      // Vérifier le tenant actuel
      const currentTenantId = tenantManager.getCurrentTenantId();
      
      if (currentTenantId) {
        // Vérifier si le tenant actuel existe toujours dans la liste des tenants
        const currentTenantExists = apiTenants.some(t => t.id === currentTenantId);
        
        if (!currentTenantExists && apiTenants.length > 0) {
          // Si le tenant actuel n'existe plus, définir le premier tenant comme tenant par défaut
          console.log(`⚠️ TenantService - Le tenant actuel ${currentTenantId} n'existe plus, passage au premier tenant disponible`);
          await this.setDefaultTenant(apiTenants[0].id);
        }
      } else if (apiTenants.length > 0) {
        // Si aucun tenant actuel n'est défini mais que des tenants sont disponibles, définir le premier comme tenant par défaut
        console.log('ℹ️ TenantService - Aucun tenant actif, définition du premier tenant comme tenant par défaut');
        await this.setDefaultTenant(apiTenants[0].id);
      }
      
      console.log(`✅ TenantService - Synchronisation terminée: ${apiTenants.length} tenant(s)`);
    } catch (error) {
      console.error('❌ TenantService - Erreur lors de la synchronisation des tenants:', error);
      // Ne pas propager l'erreur pour éviter de bloquer l'application
    }
  }

  /**
   * Supprime un tenant par son ID
   * @param tenantId ID du tenant à supprimer
   * @returns Promise avec le résultat de l'opération
   */
  public async deleteTenant(tenantId: string): Promise<{ success: boolean; message: string }> {
    try {
      // Vérifier si l'utilisateur est authentifié
      const tokenManager = await loadTokenManager();
      if (!tokenManager.isAuthenticated()) {
        console.warn('🔴 TenantService: Tentative de suppression de tenant sans authentification');
        throw new Error('Vous devez être connecté pour supprimer une organisation');
      }
      
      // Vérifier que le tenant existe et est accessible
      const tenant = await this.getTenantById(tenantId);
      if (!tenant) {
        throw new Error('Organisation non trouvée ou accès non autorisé');
      }
      
      console.log(`🗑️ TenantService - Suppression du tenant ${tenantId}...`);
      
      // Appel à l'API pour supprimer le tenant
      const result = await this.tenantAPI.deleteTenant(tenantId);
      
      if (result.success) {
        // Mettre à jour le cache local
        this.tenants = this.tenants.filter(t => t.id !== tenantId);
        console.log(`✅ TenantService - Tenant ${tenantId} supprimé avec succès`);
        
        // Si nous avons supprimé le tenant actuel, définir un autre tenant comme actif
        const currentTenantId = await tenantManager.getCurrentTenantId();
        if (currentTenantId === tenantId && this.tenants.length > 0) {
          await this.setDefaultTenant(this.tenants[0].id);
        }
      } else {
        console.error(`❌ TenantService - Échec de la suppression du tenant ${tenantId}:`, result.message);
      }
      
      return result;
    } catch (error) {
      console.error('❌ TenantService - Erreur lors de la suppression du tenant:', error);
      if (error instanceof Error) {
        return { success: false, message: error.message };
      }
      return { success: false, message: 'Une erreur est survenue lors de la suppression de l\'organisation' };
    }
  }
  
  /**
   * Crée un tenant avec un plan spécifié et des métadonnées supplémentaires
   * @param name Nom du tenant/organisation
   * @param plan Plan à utiliser ('free', 'standard', 'professional')
   * @param metadata Métadonnées supplémentaires (settings, domain, etc.)
   * @returns Promise<Tenant> Le tenant créé
   */
  public async createTenantWithPlan(
    name: string,
    plan: TenantPlan = 'free',
    metadata: {
      settings?: {
        industry?: string;
        size?: string;
        country?: string;
        website?: string;
      };
      domain?: string;
    } = {}
  ): Promise<Tenant | null> {
    // Générer un slug à partir du nom
    const slug = this.generateSlug(name);
    
    // Préparer les données du tenant
    const tenantData: Partial<Tenant> = {
      name,
      slug,
      plan,
      settings: metadata.settings || {},
      domain: metadata.domain || ''
    };
    
    // Utiliser la méthode createTenant existante
    return this.createTenant(tenantData, true);
  }

  /**
   * Vérifie si l'utilisateur peut créer un tenant avec le plan spécifié
   * @param plan Plan à vérifier ('free', 'standard', 'professional')
   * @returns Promise<boolean> true si l'utilisateur peut créer un tenant avec ce plan
   */
  public async canCreateTenantWithPlan(plan: TenantPlan): Promise<boolean> {
    try {
      // Vérifier si l'utilisateur est authentifié
      const tokenManager = await loadTokenManager();
      if (!tokenManager.isAuthenticated()) {
        return false;
      }
      
      // Pour les plans gratuits, vérifier si l'utilisateur a déjà atteint sa limite
      if (plan === 'free') {
        const tenants = await this.getAllTenants();
        const freeTenants = tenants.filter(t => t.plan === 'free' || !t.plan);
        
        // Limiter à 1 organisation gratuite par utilisateur
        return freeTenants.length < 1;
      }
      
      // Pour les autres plans, autoriser la création
      return true;
    } catch (error) {
      console.error('❌ TenantService: Erreur lors de la vérification d\'éligibilité:', error);
      return false;
    }
  }

  public async createTenant(tenantData: Partial<Tenant>, tryAlternativePlans: boolean = false): Promise<Tenant | null> {
    try {
      // Vérifier si l'utilisateur est authentifié
      const tokenManager = await loadTokenManager();
      if (!tokenManager.isAuthenticated()) {
        console.warn('🔴 TenantService: Tentative de création de tenant sans authentification');
        throw new Error('Vous devez être connecté pour créer une organisation');
      }
      
      // Valider les données du tenant
      if (!tenantData.name) {
        throw new Error('Le nom de l\'organisation est requis');
      }
      
      // Générer un slug si non fourni
      if (!tenantData.slug) {
        tenantData.slug = this.generateSlug(tenantData.name);
      }
      
      // Définir le status par défaut si non fourni
      if (!tenantData.status) {
        tenantData.status = TenantStatus.ACTIVE as any; // Cast to any to bypass type checking
      }
      
      // Création d'un nouveau tenant dans l'API
      console.log(`🏗️ TenantService - Création du tenant...`, tenantData);
      const userId = await this.getCurrentUserId();
      const newTenant = await this.tenantAPI.createTenant(tenantData, userId);
      console.log(`✅ TenantService - Tenant créé avec succès:`, newTenant);
      
      // Mise à jour du cache et définition comme tenant par défaut
      this.tenants = [...this.tenants, newTenant];
      await this.setDefaultTenant(newTenant.id);
      return newTenant;
    } catch (error) {
      console.error('❌ TenantService: Erreur lors de la création du tenant:', error);
      
      // Si option activée, essayer avec des plans alternatifs
      if (tryAlternativePlans && error instanceof Error && error.message.includes('plan')) {
        console.log('🔄 TenantService: Tentative avec un plan alternatif...');
        
        // Si échec avec le plan professionnel, essayer avec le plan standard
        if (tenantData.plan === 'professional') {
          console.log('🔄 TenantService: Rétrogradation du plan professionnel vers standard');
          tenantData.plan = 'standard';
          
          // Nouvelle tentative avec le plan standard
          try {
            return await this.createTenant(tenantData, false);
          } catch (retryError) {
            console.error('❌ TenantService: Échec également avec le plan standard:', retryError);
            throw retryError;
          }
        }
        
        // Si échec avec le plan standard, essayer avec le plan gratuit
        if (tenantData.plan === 'standard') {
          console.log('🔄 TenantService: Rétrogradation du plan standard vers gratuit');
          tenantData.plan = 'free';
          
          try {
            return await this.createTenant(tenantData, false);
          } catch (retryError) {
            console.error('❌ TenantService: Échec également avec le plan gratuit:', retryError);
            throw retryError;
          }
        }
      }
      
      // Propager l'erreur
      throw error;
    }
  }
}

// Exporter l'instance singleton par défaut
export default TenantService.getInstance();
